import {
  ORDER_EVENT_STATUS_APPROVED,
  ORDER_EVENT_STATUS_COMPLETED,
  ORDER_EVENT_STATUS_DENIED,
  ORDER_EVENT_STATUS_EXPIRED,
  ORDER_EVENT_STATUS_PUBLISHED
} from '@klear/constants/order-event-status'
import { FUNDED_OFFER_STATUSES } from '@klear/invoice-offer/invoice-offer'
import {
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ACTION_RESET,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ACTION_VOID,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import { type OrderEventActionEnum, type OrderEventType } from '@klear/order-event/constants'
import {
  lifecycleActionPermission,
  orderEventLifecycleActionsFrom,
  type OrderEventLifecycleAction,
  type OrderEventLifecycleState
} from '@klear/order-event-lifecycle/lifecycle'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { orderEventActionLabel } from '@klear/order-event/presentation'
import { timelineItemId, timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'
import { flattenTimelineEvents } from '@klear/order-event-ui/event-timeline-utils'
import { hasEventFormHandler } from '@klear/order-event-ui/event-handlers/registry'

type ActionUser = { hasPermission(entity: string | undefined, action: string | undefined): boolean }

type ActionEvent = {
  id: number
  type: OrderEventTimelineItem['type']
  lifecycleState: OrderEventLifecycleState
  parentId?: number
  isFundedOffer: boolean
  readyToFund: boolean
}

export type TimelineActionContext = {
  timeline: readonly OrderEventTimelineItem[]
  byId: ReadonlyMap<number, ActionEvent>
  childrenByParent: ReadonlyMap<number, readonly ActionEvent[]>
  invoiceIdsWithFundedOffer: ReadonlySet<number>
  invoiceIdsWithProof: ReadonlySet<number>
  editProtectedIds: ReadonlySet<number>
}

function toActionEvent(entry: OrderEventTimelineItem): ActionEvent {
  return {
    id: timelineItemId(entry),
    type: entry.type,
    lifecycleState: entry.envelope.lifecycleState,
    parentId: timelineItemParentId(entry),
    isFundedOffer: entry.type === PERMISSION_ENTITY_INVOICE_OFFER && entry.model.isFunded,
    readyToFund: entry.type === PERMISSION_ENTITY_INVOICE_OFFER && entry.model.readyToFund
  }
}

function hasState(event: ActionEvent, ...states: OrderEventLifecycleState[]): boolean {
  return states.includes(event.lifecycleState)
}

function isInvoice(event: ActionEvent): boolean {
  return event.type === PERMISSION_ENTITY_INVOICE
}

function isInvoiceOffer(event: ActionEvent): boolean {
  return event.type === PERMISSION_ENTITY_INVOICE_OFFER
}

function isProof(event: ActionEvent): boolean {
  return event.type === PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
}

function isPurchaseOrder(event: ActionEvent): boolean {
  return event.type === PERMISSION_ENTITY_PURCHASE_ORDER
}

export function buildTimelineActionContext(timeline: readonly OrderEventTimelineItem[]): TimelineActionContext {
  const events = flattenTimelineEvents(timeline).map(toActionEvent)
  const byId = new Map(events.map((event) => [event.id, event]))
  const childrenByParent = new Map<number, ActionEvent[]>()
  for (const event of events) {
    if (event.parentId == null || event.parentId <= 0) continue
    const list = childrenByParent.get(event.parentId) ?? []
    list.push(event)
    childrenByParent.set(event.parentId, list)
  }

  const invoiceIdsWithFundedOffer = new Set<number>()
  const invoiceIdsWithProof = new Set<number>()
  const editProtectedIds = new Set<number>()
  for (const event of events) {
    if (event.isFundedOffer && event.parentId != null) {
      invoiceIdsWithFundedOffer.add(event.parentId)
    }
    if (isProof(event) && event.parentId != null) invoiceIdsWithProof.add(event.parentId)
    if (!hasState(event, ...FUNDED_OFFER_STATUSES)) continue
    let parentId = event.parentId
    while (parentId != null && !editProtectedIds.has(parentId)) {
      editProtectedIds.add(parentId)
      parentId = byId.get(parentId)?.parentId
    }
  }

  return {
    timeline,
    byId,
    childrenByParent,
    invoiceIdsWithFundedOffer,
    invoiceIdsWithProof,
    editProtectedIds
  }
}

function hasFundedOfferForInvoice(context: TimelineActionContext, invoiceId: number): boolean {
  return context.invoiceIdsWithFundedOffer.has(invoiceId)
}

function isEditProtected(context: TimelineActionContext, eventId: number): boolean {
  return context.editProtectedIds.has(eventId)
}

function parentLifecycleState(context: TimelineActionContext, event: ActionEvent) {
  return event.parentId == null ? undefined : context.byId.get(event.parentId)?.lifecycleState
}

function domainAllowsLifecycleAction(
  context: TimelineActionContext,
  event: ActionEvent,
  action: OrderEventLifecycleAction
): boolean {
  const parentState = parentLifecycleState(context, event)
  if (action === PERMISSION_ACTION_PUBLISH && parentState != null && !isProof(event)) {
    return parentState === ORDER_EVENT_STATUS_PUBLISHED || parentState === ORDER_EVENT_STATUS_APPROVED
  }

  if (isInvoice(event)) {
    if (action === PERMISSION_ACTION_APPROVE || action === PERMISSION_ACTION_DENY) {
      return !hasFundedOfferForInvoice(context, event.id)
    }
  }

  if (isProof(event)) {
    if (action === PERMISSION_ACTION_APPROVE || action === PERMISSION_ACTION_DENY) {
      return !hasFundedOfferForInvoice(context, event.parentId ?? 0)
    }
  }

  if (isInvoiceOffer(event)) {
    if (action === PERMISSION_ACTION_ACCEPT) return parentState !== ORDER_EVENT_STATUS_EXPIRED
    if (action === PERMISSION_ACTION_FUND) return event.readyToFund
    if (action === PERMISSION_ACTION_VOID) return !isEditProtected(context, event.id)
  }

  return true
}

function canEdit(context: TimelineActionContext, eventId: number): boolean {
  const event = context.byId.get(eventId)
  if (!event) return false
  if (!hasEventFormHandler(event.type)) return false
  if (isInvoice(event) && hasFundedOfferForInvoice(context, event.id)) return false
  if (isProof(event)) {
    const parentInvoiceId = event.parentId
    if (parentInvoiceId != null && hasFundedOfferForInvoice(context, parentInvoiceId)) return false
    return !hasState(event, ORDER_EVENT_STATUS_COMPLETED)
  }
  return (
    !hasState(event, ORDER_EVENT_STATUS_COMPLETED) && (isPurchaseOrder(event) || !isEditProtected(context, eventId))
  )
}

function canDelete(context: TimelineActionContext, eventId: number): boolean {
  const event = context.byId.get(eventId)
  if (!event) return false
  return !hasState(event, ORDER_EVENT_STATUS_COMPLETED) && !context.childrenByParent.has(eventId)
}

export const ORDER_EVENT_VIEW_TARGETS = [PERMISSION_ENTITY_INVOICE_OFFER] as const

export type OrderEventViewTarget = (typeof ORDER_EVENT_VIEW_TARGETS)[number]

export type TimelineActionOption = {
  label: string
  value: OrderEventActionEnum
  viewTarget?: OrderEventViewTarget
  addEventType?: OrderEventType
}

function viewOptions(event: ActionEvent): TimelineActionOption[] {
  if (!isInvoiceOffer(event)) {
    return [{ label: orderEventActionLabel(event.type, PERMISSION_ACTION_VIEW), value: PERMISSION_ACTION_VIEW }]
  }
  return [
    { label: 'View Detail', value: PERMISSION_ACTION_VIEW },
    { label: 'View Offer', value: PERMISSION_ACTION_VIEW, viewTarget: PERMISSION_ENTITY_INVOICE_OFFER }
  ]
}

function addActionLabelsForInvoice(
  context: TimelineActionContext,
  eventId: number,
  actions: OrderEventActionEnum[],
  toOption: (action: OrderEventActionEnum, index: number) => TimelineActionOption
): TimelineActionOption[] {
  const hasProof = context.invoiceIdsWithProof.has(eventId)
  const addActionIndices = actions.map((a, i) => (a === PERMISSION_ACTION_ADD ? i : -1)).filter((i) => i !== -1)

  return actions.map((action, index) => {
    if (action !== PERMISSION_ACTION_ADD) return toOption(action, index)

    let addEventType: OrderEventType = hasProof
      ? PERMISSION_ENTITY_INVOICE_OFFER
      : PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
    if (addActionIndices.length > 1) {
      addEventType =
        addActionIndices[0] === index ? PERMISSION_ENTITY_INVOICE_OFFER : PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
    }
    const label = addEventType === PERMISSION_ENTITY_INVOICE_OFFER ? 'Add invoice offer' : 'Add proof of acceptance'
    return { label, value: action, addEventType }
  })
}

function canTakeLifecycleAction(
  user: ActionUser,
  context: TimelineActionContext,
  event: ActionEvent,
  action: OrderEventLifecycleAction
): boolean {
  return (
    orderEventLifecycleActionsFrom(event.type, event.lifecycleState).includes(action) &&
    domainAllowsLifecycleAction(context, event, action) &&
    user.hasPermission(event.type, lifecycleActionPermission(action, event.lifecycleState))
  )
}

export function availableActionsFromTimeline(
  user: ActionUser,
  context: TimelineActionContext,
  eventId: number
): TimelineActionOption[] {
  const event = context.byId.get(eventId)
  if (!event) return []

  const state = event.lifecycleState
  const allows = (action: OrderEventLifecycleAction): boolean => canTakeLifecycleAction(user, context, event, action)

  const actions: OrderEventActionEnum[] = []
  if (user.hasPermission(event.type, PERMISSION_ACTION_VIEW)) {
    actions.push(PERMISSION_ACTION_VIEW)
  }
  if (canEdit(context, eventId) && user.hasPermission(event.type, PERMISSION_ACTION_EDIT)) {
    actions.push(PERMISSION_ACTION_EDIT)
  }
  if (allows(PERMISSION_ACTION_PUBLISH)) actions.push(PERMISSION_ACTION_PUBLISH)
  if (allows(PERMISSION_ACTION_ACCEPT)) actions.push(PERMISSION_ACTION_ACCEPT)
  if (allows(PERMISSION_ACTION_REJECT)) actions.push(PERMISSION_ACTION_REJECT)
  if (allows(PERMISSION_ACTION_APPROVE)) actions.push(PERMISSION_ACTION_APPROVE)
  if (allows(PERMISSION_ACTION_DENY)) actions.push(PERMISSION_ACTION_DENY)
  if (
    isInvoice(event) &&
    state !== ORDER_EVENT_STATUS_DENIED &&
    state !== ORDER_EVENT_STATUS_EXPIRED &&
    user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_ADD)
  ) {
    actions.push(PERMISSION_ACTION_ADD)
  }
  if (
    isInvoice(event) &&
    !context.invoiceIdsWithProof.has(event.id) &&
    user.hasPermission(PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE, PERMISSION_ACTION_ADD)
  ) {
    actions.push(PERMISSION_ACTION_ADD)
  }
  if (allows(PERMISSION_ACTION_FUND)) actions.push(PERMISSION_ACTION_FUND)
  if (allows(PERMISSION_ACTION_COMPLETE)) actions.push(PERMISSION_ACTION_COMPLETE)
  if (allows(PERMISSION_ACTION_VOID)) actions.push(PERMISSION_ACTION_VOID)
  if (canDelete(context, eventId) && user.hasPermission(event.type, PERMISSION_ACTION_DELETE)) {
    actions.push(PERMISSION_ACTION_DELETE)
  }

  const toOption = (action: OrderEventActionEnum): TimelineActionOption => ({
    label: orderEventActionLabel(event.type, action),
    value: action
  })

  let options =
    isInvoice(event) && actions.includes(PERMISSION_ACTION_ADD)
      ? addActionLabelsForInvoice(context, eventId, actions, toOption)
      : actions.flatMap((action) => (action === PERMISSION_ACTION_VIEW ? viewOptions(event) : [toOption(action)]))

  if (isPurchaseOrder(event)) {
    const hasFundedOfferUnderPo = (context.childrenByParent.get(eventId) ?? [])
      .filter(isInvoice)
      .some((invoice) => context.invoiceIdsWithFundedOffer.has(invoice.id))
    const isPublished = hasState(event, ORDER_EVENT_STATUS_PUBLISHED)
    if (hasFundedOfferUnderPo && !isPublished) {
      options = options.filter((o) => o.value !== PERMISSION_ACTION_EDIT)
    }
  }

  return options
}

export function canUndoRejectionFromTimeline(
  user: ActionUser,
  context: TimelineActionContext,
  eventId: number
): boolean {
  const event = context.byId.get(eventId)
  if (!event || event.lifecycleState !== ORDER_EVENT_STATUS_DENIED) return false
  return canTakeLifecycleAction(user, context, event, PERMISSION_ACTION_RESET)
}

export function defaultAddEventTypeFromTimeline(context: TimelineActionContext, eventId: number): OrderEventType {
  const hasProof = context.invoiceIdsWithProof.has(eventId)
  return hasProof ? PERMISSION_ENTITY_INVOICE_OFFER : PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
}
